import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type { IssuerScopeProposal, ScopeChain } from "../../../../../api/types"
import type { StatementCard } from "../useStudioV2State"

/** A search the statement's own document suggests, never a claim on
 *  articles, a query the reviewer runs and confirms. `terms` are OR-ed over
 *  article number, name and supplier code (`articleSearch.referenceMatches`). */
export interface SearchSuggestion {
  label: string
  terms: string[]
  /** The document span the resolver cited for this query: the quick view's
   *  tooltip names it. Absent for an unresolved reference: its own words ARE
   *  the label. */
  cite?: string | null
  /** A fixed article set instead of a term search: the view shows exactly
   *  these numbers (the issuer's articles the server matched,
   *  `issuerSuggestion`) and offers to stage exactly them. `terms` then only
   *  highlight. */
  articleNumbers?: string[]
}

type Translate = (
  key: TranslationKeys,
  values?: Record<string, string | number>,
) => string

/** The resolver's columns a suggestion can actually find (`referenceMatches`:
 *  article number, name, supplier code). `id` is a
 *  master-data UUID the reviewer never sees, so a filter over it is no
 *  search a human could run. */
const SEARCHABLE_COLUMNS = new Set([
  "part_number",
  "supplier_part_number",
  "name",
  "description",
])

/**
 * The searches the document itself suggests for one statement, two sources,
 * both straight from the extraction pipeline, nothing here is derived anew:
 *
 * 1. The references the document names that M2 could NOT bind to a live
 *    article (`scope_resolutions[key].unresolved`), one search each. Those
 *    are exactly the words the reviewer would otherwise type into the find
 *    bar by hand.
 * 2. The resolver's OWN filter chain (`scope_resolutions[key].chain`,
 *    `chainSuggestions` below): the query M2 actually ran over the article
 *    table when it bound the statement: the article numbers the document
 *    lists (as one search, not one per number), a family or product term it
 *    recognised (`contains` over a name/description column), and the
 *    exceptions the document names (an `exclude` step). Each cites the span
 *    that made the resolver write it.
 *
 * Product decision (Pablo, 2026-09-16): these replace the mask's former
 * "Produktgruppen" band, which had no product-group data behind it. Only
 * information the pipeline already produced is offered; a run whose resolver
 * escalated (no chain) contributes nothing beyond its unresolved references.
 */
export function searchSuggestions(
  card: StatementCard,
  translate: Translate,
): SearchSuggestion[] {
  const seen = new Set<string>()
  const out: SearchSuggestion[] = []
  const push = (s: SearchSuggestion) => {
    const key = s.terms
      .map((t) => t.toLowerCase())
      .sort()
      .join(" | ")
    if (seen.has(key)) return
    seen.add(key)
    out.push(s)
  }
  for (const raw of card.unresolvedRefTexts ?? []) {
    const text = raw.trim()
    if (text) push({ label: text, terms: [text] })
  }
  for (const s of chainSuggestions(card.scopeChain ?? null, translate)) push(s)
  return out
}

type CodeBucket = { values: string[]; cites: string[] }

/** The chain's steps as searches. Identity-code steps (`equals`/`in` over a
 *  code column) fold into ONE search per action: the document's article
 *  list is the query, five single-number views would be noise, while a
 *  `contains` term stays its own search under its own word. `intersect`
 *  narrows what `add` selected and reads as the same kind of query. */
function chainSuggestions(
  chain: ScopeChain | null,
  translate: Translate,
): SearchSuggestion[] {
  if (!chain) return []
  const out: SearchSuggestion[] = []
  const codes: Record<"add" | "exclude", CodeBucket> = {
    add: { values: [], cites: [] },
    exclude: { values: [], cites: [] },
  }
  for (const step of chain.steps ?? []) {
    const f = step.filter
    if (!f?.column || !SEARCHABLE_COLUMNS.has(f.column)) continue
    const action = step.action === "exclude" ? "exclude" : "add"
    const cite = step.citation?.span?.trim() || null
    if (f.kind === "contains") {
      const term = f.value?.trim()
      if (!term) continue
      out.push({
        label:
          action === "exclude"
            ? translate("MC_DECL_V2_BROWSER_SUGGESTION_TERM_EXCLUDED", { term })
            : term,
        terms: [term],
        cite,
      })
      continue
    }
    const raw =
      f.kind === "equals" ? [f.value] : f.kind === "in" ? (f.values ?? []) : []
    const values = raw.map((v) => (v ?? "").trim()).filter(Boolean)
    if (values.length === 0) continue
    const bucket = codes[action]
    for (const v of values) {
      if (!bucket.values.some((x) => x.toLowerCase() === v.toLowerCase()))
        bucket.values.push(v)
    }
    if (cite && !bucket.cites.includes(cite)) bucket.cites.push(cite)
  }
  for (const action of ["add", "exclude"] as const) {
    const bucket = codes[action]
    if (bucket.values.length === 0) continue
    out.push({
      label: translate(
        action === "exclude"
          ? "MC_DECL_V2_BROWSER_SUGGESTION_NUMBERS_EXCLUDED"
          : "MC_DECL_V2_BROWSER_SUGGESTION_NUMBERS",
        { n: bucket.values.length },
      ),
      terms: bucket.values,
      cite: bucket.cites.length > 0 ? bucket.cites.join(" · ") : null,
    })
  }
  return out
}

/** Whether the proposal has a match. THE ONE TEST: the note's button opens
 *  suggestion 0 (`ISSUER_SUGGESTION_VIEW`) only when `issuerSuggestion` put the
 *  issuer there, so the two must never disagree. */
export function hasIssuerMatch(
  proposal: IssuerScopeProposal | null | undefined,
): proposal is IssuerScopeProposal & { matchedValue: string } {
  return (
    !!proposal?.field && !!proposal.matchedValue && proposal.matchedCount > 0
  )
}

/**
 * MC_ISSUER_SCOPE_PROPOSAL (TUR-1720): the issuer's own articles in this
 * supplier's pool as the FIRST search the browser offers, the same quick view
 * every other suggestion is. Opening it selects nothing; the reviewer stages
 * it (ticks, or the view's "select only" on a statement that starts from the
 * whole pool) and applies through the browser's own Apply, so the decision
 * lands through the existing, audited scope write. The server did the matching
 * (`mc_issuer_scope`), the client only shows its article set. Null without a
 * match.
 */
export function issuerSuggestion(
  proposal: IssuerScopeProposal | null | undefined,
  translate: Translate,
  poolNumbers: string[],
): SearchSuggestion | null {
  if (!hasIssuerMatch(proposal)) return null
  return {
    label: translate("MC_DECL_V2_ISSUER_SUGGESTION", {
      value: proposal.matchedValue,
    }),
    terms: [proposal.matchedValue],
    // Empty on a match of the whole pool: the server does not repeat the pool.
    articleNumbers:
      proposal.articleNumbers.length > 0
        ? proposal.articleNumbers
        : poolNumbers,
    // The tooltip's "per the document" line: the issuer as the run read it.
    cite: proposal.issuerName,
  }
}
